/**
 * THE SITE'S FOOTER, AND THE GAP ABOVE IT, ON AN EXPERIENCE'S PAGE.
 *
 * lib/chrome.ts leaves the footer the church's on ordinary event pages, on
 * purpose: it is the church's furniture three screens below the artwork. An
 * experience is different: the whole page is designed, and a navy footer
 * under Garden Paper reads as another site. So an experience may name the
 * footer's ground and its rule, for its own page only.
 *
 * On woodsidebible.org the footer is a Salient global section
 * (`.nectar_hook_global_section_footer`): full-width rows that each paint the
 * navy, an inner `.row-bg` that paints it again, and a teal `.divider-border`.
 * Like lib/chrome.ts, the stylesheet is a constant and values arrive as
 * custom properties after a hex check; the undo takes everything back off.
 *
 * THE GAP. The WordPress page puts its own row margins and padding under the
 * widget (114px on this page), so the paper stopped short and white showed
 * before the footer. The experience root reaches down over that space:
 * padding-bottom grows by the gap and margin-bottom takes the same amount
 * back, so the page's layout, and the footer, do not move.
 */
import { useEffect, type RefObject } from 'react';
import { isHexColor } from '@/lib/accent';

const FLAG = 'data-xp-footer';
const STYLE_ID = 'xp-site-footer';
const FOOTER = '.nectar_hook_global_section_footer';

const CSS = `
html[${FLAG}] ${FOOTER} .wpb_row.full-width-section,
html[${FLAG}] ${FOOTER} .row-bg.using-bg-color {
  background-color: var(--xp-footer-bg) !important;
}
html[${FLAG}] ${FOOTER} .divider-border {
  background-color: var(--xp-footer-rule) !important;
  border-color: var(--xp-footer-rule) !important;
}
`;

export function useSiteFooter(
  root: RefObject<HTMLElement | null>,
  footer: { background: string; rule: string } | undefined
) {
  // The colours.
  useEffect(() => {
    const html = document.documentElement;
    if (!footer || !isHexColor(footer.background) || !isHexColor(footer.rule)) return;
    if (!document.querySelector(FOOTER)) return;
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.appendChild(style);
    }
    html.style.setProperty('--xp-footer-bg', footer.background);
    html.style.setProperty('--xp-footer-rule', footer.rule);
    html.setAttribute(FLAG, '');
    return () => {
      html.removeAttribute(FLAG);
      html.style.removeProperty('--xp-footer-bg');
      html.style.removeProperty('--xp-footer-rule');
      document.getElementById(STYLE_ID)?.remove();
    };
  }, [footer?.background, footer?.rule]);

  // The reach down to it.
  useEffect(() => {
    const el = root.current;
    const target =
      document.querySelector<HTMLElement>(FOOTER) ?? document.getElementById('footer-outer');
    if (!el || !target) return;
    const base = parseFloat(getComputedStyle(el).paddingBottom) || 0;
    let reach = 0;
    const measure = () => {
      // Measured with our own reach taken back off, so it never measures itself.
      const bottom = el.getBoundingClientRect().bottom - reach;
      const gap = Math.max(0, Math.round(target.getBoundingClientRect().top - bottom));
      if (gap === reach) return;
      reach = gap;
      el.style.paddingBottom = `${base + gap}px`;
      el.style.marginBottom = `${-gap}px`;
    };
    measure();
    // Net height never changes (padding in, margin out), so watching the
    // page cannot feed itself.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      observer.disconnect();
      el.style.paddingBottom = '';
      el.style.marginBottom = '';
    };
  }, [root]);
}
